import { defineStore } from 'pinia';
import AiInsightsAPI from 'dashboard/api/aiInsights';

export const useAiInsightsStore = defineStore('aiInsights', {
  state: () => ({
    agents: [],
    analysesByConversation: {},
    stats: null,
    uiFlags: {
      fetchingAgents: false,
      creatingAgent: false,
      updatingAgent: false,
      deletingAgent: false,
      runningAnalysis: false,
      fetchingAnalyses: false,
      fetchingStats: false,
    },
  }),

  getters: {
    getAgents: state => state.agents,
    getActiveAgents: state => state.agents.filter(a => a.active),
    getAnalysesForConversation: state => conversationId =>
      state.analysesByConversation[conversationId] || [],
  },

  actions: {
    async fetchAgents() {
      this.uiFlags.fetchingAgents = true;
      try {
        const { data } = await AiInsightsAPI.getAgents();
        this.agents = data;
      } finally {
        this.uiFlags.fetchingAgents = false;
      }
    },

    async createAgent(payload) {
      this.uiFlags.creatingAgent = true;
      try {
        const { data } = await AiInsightsAPI.createAgent(payload);
        this.agents.unshift(data);
        return data;
      } finally {
        this.uiFlags.creatingAgent = false;
      }
    },

    async updateAgent(agentId, payload) {
      this.uiFlags.updatingAgent = true;
      try {
        const { data } = await AiInsightsAPI.updateAgent(agentId, payload);
        const index = this.agents.findIndex(a => a.id === agentId);
        if (index !== -1) this.agents[index] = data;
        return data;
      } finally {
        this.uiFlags.updatingAgent = false;
      }
    },

    async deleteAgent(agentId) {
      this.uiFlags.deletingAgent = true;
      try {
        await AiInsightsAPI.deleteAgent(agentId);
        this.agents = this.agents.filter(a => a.id !== agentId);
      } finally {
        this.uiFlags.deletingAgent = false;
      }
    },

    async fetchAnalyses(conversationId) {
      this.uiFlags.fetchingAnalyses = true;
      try {
        const { data } = await AiInsightsAPI.getAnalyses(conversationId);
        this.analysesByConversation[conversationId] = data;
      } finally {
        this.uiFlags.fetchingAnalyses = false;
      }
    },

    async runAnalysis(conversationId, agentId) {
      this.uiFlags.runningAnalysis = true;
      try {
        const { data } = await AiInsightsAPI.runAnalysis(conversationId, agentId);
        if (!this.analysesByConversation[conversationId]) {
          this.analysesByConversation[conversationId] = [];
        }
        this.analysesByConversation[conversationId].unshift(data);
        return data;
      } finally {
        this.uiFlags.runningAnalysis = false;
      }
    },

    async pollAnalysis(conversationId, analysisId) {
      const { data } = await AiInsightsAPI.getAnalysis(conversationId, analysisId);
      const analyses = this.analysesByConversation[conversationId] || [];
      const index = analyses.findIndex(a => a.id === analysisId);
      if (index !== -1) {
        this.analysesByConversation[conversationId][index] = data;
      }
      return data;
    },

    upsertAnalysis(conversationId, analysis) {
      if (!this.analysesByConversation[conversationId]) {
        this.analysesByConversation[conversationId] = [];
      }
      const index = this.analysesByConversation[conversationId].findIndex(
        a => a.id === analysis.id
      );
      if (index !== -1) {
        this.analysesByConversation[conversationId][index] = analysis;
      } else {
        this.analysesByConversation[conversationId].unshift(analysis);
      }
    },

    async fetchStats(params = {}) {
      this.uiFlags.fetchingStats = true;
      try {
        const { data } = await AiInsightsAPI.getStats(params);
        this.stats = data;
      } finally {
        this.uiFlags.fetchingStats = false;
      }
    },
  },
});
